// =============================================================================
// T-482 — « Suivi MaPrimeRénov' » (Hub Conformité, tenants dont le régime d'aides déclare `mpr` : Climat)
// Une ligne par dossier : où en est sa demande MaPrimeRénov' (non déposé → en instruction → accepté → prime versée), les dates et les montants.
// Source : GET /api/metier/aides/suivi-mpr (champ `donnees_mpr` du dossier, saisi dans l'onglet « Données MPR »). Lecture seule : on modifie dans le dossier.
// ⛔ Aucune donnée fiscale ni de santé. Grille = OHGrid (jamais une grille de plus).
// =============================================================================
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Landmark, ExternalLink, Download, FolderOpen, Hourglass, CheckCircle2, XCircle, Euro } from "lucide-react";
import { OHGrid } from "../../components/ui/oh-grid";
import { TintedBadge } from "../../components/ui/oh-tinted";
import { API, getAuthHeaders } from "../metier/dossier/helpers";
import { formatDateFR } from "../../utils/dateFormat";

export const STATUTS_MPR = {
  non_depose: { label: "Non déposé", color: "gray", icone: FolderOpen },
  en_cours: { label: "En cours d'instruction", color: "amber", icone: Hourglass },
  accepte: { label: "Accepté", color: "green", icone: CheckCircle2 },
  refuse: { label: "Refusé", color: "red", icone: XCircle },
  paye: { label: "Prime versée", color: "blue", icone: Euro },
};
const MESSAGE_ERREUR = {
  401: "Session expirée : reconnectez-vous pour voir le suivi MaPrimeRénov'.",
  403: "Votre rôle n'a pas accès au suivi MaPrimeRénov'.",
};
export const euros = (v) => (v == null || v === "" ? "—" : `${Number(v).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`);

// Export CSV — même forme que HubGrid : BOM, séparateur « ; », guillemets doublés.
export function construireCsv(rows) {
  const colonnes = [
    ["Dossier", (r) => r.reference], ["Client", (r) => r.client], ["Statut MaPrimeRénov'", (r) => STATUTS_MPR[r.statut_mpr]?.label || r.statut_mpr],
    ["N° de dossier MaPrimeRénov'", (r) => r.numero_dossier_mpr], ["Date de dépôt", (r) => formatDateFR(r.date_depot)],
    ["Date d'acceptation", (r) => formatDateFR(r.date_acceptation)], ["Montant estimé", (r) => euros(r.montant_estime)], ["Montant accordé", (r) => euros(r.montant_accorde)],
  ];
  const q = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return "﻿" + [colonnes.map(([t]) => q(t)).join(";"), ...rows.map((r) => colonnes.map(([, f]) => q(f(r))).join(";"))].join("\n");
}
function telecharger(rows) {
  const blob = new Blob([construireCsv(rows)], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `suivi_maprimerenov_${new Date().toISOString().split("T")[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function SuiviMprPage({ tenantId, onOpenDossier }) {
  const [lignes, setLignes] = useState([]);
  const [totalAccorde, setTotalAccorde] = useState(0);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const visibles = useRef([]);
  const surLignesVisibles = useCallback((rows) => { visibles.current = rows; }, []);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const res = await fetch(`${API}/api/metier/aides/suivi-mpr`, { headers: getAuthHeaders(tenantId), credentials: "include" });
      if (!res.ok) {
        setLignes([]);
        setErreur(MESSAGE_ERREUR[res.status] || `Chargement impossible (erreur ${res.status}).`);
        return;
      }
      const data = await res.json();
      setLignes(Array.isArray(data.lignes) ? data.lignes : []);
      setTotalAccorde(Number(data.montant_accorde_total || 0));
    } catch {
      setLignes([]);
      setErreur("Chargement impossible : le serveur ne répond pas.");
    } finally {
      setChargement(false);
    }
  }, [tenantId]);
  useEffect(() => { charger(); }, [charger]);

  const columns = useMemo(() => [
    { key: "reference", label: "Dossier",
      filter: { type: "text", placeholder: "Dossier…" },
      render: (v, r) => (
        <span style={{ color: "var(--oh-text)" }}>
          {v || "—"}
          {r.client && <span className="block text-xs" style={{ color: "var(--oh-text-muted)" }}>{r.client}</span>}
        </span>
      ) },
    { key: "statut_mpr", label: "Où en est la demande",
      filter: { type: "select", value: (r) => r.statut_mpr, options: Object.entries(STATUTS_MPR).map(([value, e]) => ({ value, label: e.label })) },
      sortValue: (r) => Object.keys(STATUTS_MPR).indexOf(r.statut_mpr),
      render: (v) => { const s = STATUTS_MPR[v]; return s ? <TintedBadge color={s.color}>{s.label}</TintedBadge> : (v || "—"); } },
    { key: "numero_dossier_mpr", label: "N° MaPrimeRénov'", render: (v) => v || "—" },
    { key: "date_depot", label: "Déposé le", render: (v) => formatDateFR(v) || "—" },
    { key: "date_acceptation", label: "Accepté le", render: (v) => formatDateFR(v) || "—" },
    { key: "montant_estime", label: "Montant estimé", sortValue: (r) => Number(r.montant_estime || 0), render: (v) => euros(v) },
    { key: "montant_accorde", label: "Montant accordé", sortValue: (r) => Number(r.montant_accorde || 0), render: (v) => euros(v) },
  ], []);

  const stats = useMemo(() => [
    { key: "total", label: "Dossiers", value: lignes.length, icon: Landmark, color: "blue", filter: () => true },
    ...Object.entries(STATUTS_MPR).map(([code, e]) => ({
      key: code, label: e.label, icon: e.icone, color: e.color,
      value: lignes.filter((l) => l.statut_mpr === code).length, filter: (l) => l.statut_mpr === code,
    })),
  ], [lignes]);

  const rowActions = useMemo(() => (onOpenDossier
    ? [{ label: "Ouvrir le dossier", icon: ExternalLink, onClick: (l) => onOpenDossier(l.dossier_id), show: (l) => !!l.dossier_id }]
    : []), [onOpenDossier]);
  const bulkActions = useMemo(() => [
    { label: (n) => `Exporter la sélection (${n})`, icon: Download, onClick: (ids) => telecharger(lignes.filter((l) => ids.includes(l.id))) },
  ], [lignes]);
  const headerActions = useMemo(() => [
    { label: "Exporter (CSV)", icon: Download, onClick: () => telecharger(visibles.current) },
  ], []);

  return (
    <div className="space-y-4 p-4" data-testid="suivi-mpr-page">
      <div className="flex items-center gap-3">
        <Landmark className="w-5 h-5" style={{ color: "var(--oh-accent-text)" }} />
        <div>
          <h1 className="text-lg font-medium" style={{ color: "var(--oh-text)" }}>Suivi MaPrimeRénov'</h1>
          <p className="text-sm" style={{ color: "var(--oh-text-secondary)" }} data-testid="suivi-mpr-phrase">
            Pour chaque dossier, où en est la demande d'aide : non déposée, en instruction, acceptée ou prime versée. Pour la mettre à jour,
            ouvrez le dossier, onglet « Données MPR ». Total accordé : <span data-testid="suivi-mpr-total">{euros(totalAccorde)}</span>.
          </p>
        </div>
      </div>
      {erreur && (
        <div role="alert" data-testid="suivi-mpr-erreur" className="rounded-lg px-4 py-3 text-sm"
          style={{ background: "var(--oh-error-bg)", color: "var(--oh-error-text)", border: "1px solid var(--oh-error-border)" }}>
          {erreur}
        </div>
      )}
      <OHGrid
        testIdPrefix="suivi-mpr"
        data={lignes}
        columns={columns}
        stats={stats}
        loading={chargement}
        onRefresh={charger}
        defaultSortKey="created_at"
        defaultSortDir="desc"
        searchPlaceholder="Rechercher un dossier ou un client…"
        searchFilter={(l, q) => `${l.client || ""} ${l.reference || ""} ${l.numero_dossier_mpr || ""}`.toLowerCase().includes(q)}
        emptyMessage={erreur ? "Aucune donnée à afficher" : "Aucun dossier pour le moment"}
        rowActions={rowActions}
        selectable
        bulkActions={bulkActions}
        headerActions={headerActions}
        onRowsChange={surLignesVisibles}
        pagination
        pageSize={50}
      />
    </div>
  );
}
